import { useState } from 'react';
import useApiRequest from '../../../../lib/hooks/useRequest';
import { useArchiveList } from '../../hooks/use-archive-list';
import { UpdateArchiveRequestDto, UpdateContractAttachmentRequestDto } from '../../types/request';
import { ContractAttachmentFileUrlResponseDto, ContractAttachmentListItemResponseDto, ContractAttachmentResponseDto } from '../../types/response';
import ArchiveConfirmDialog from '../archive-confirm-dialog/archive-confirm-dialog';
import AttachmentFormDialog, { AttachmentFormSubmitPayload } from '../attachment-form-dialog/attachment-form-dialog';
import ListToolbar from '../list-toolbar/list-toolbar';
import AttachmentList from './attachment-list';

/** Data owner of the mail-attachments tab. */
const AttachmentListWrapper = () => {
  const { apiRequest, apiFileRequest } = useApiRequest();
  const { items, pageCount, isLoading, reload, filter, handleFilterChange, pagination, handlePaginationChange } =
    useArchiveList<ContractAttachmentListItemResponseDto>('contract-attachments/admin');

  const [isFormOpen, setIsFormOpen] = useState(false);
  // No `GET :id` for attachments – the list item carries everything the edit form needs.
  const [editingAttachment, setEditingAttachment] = useState<ContractAttachmentListItemResponseDto | null>(null);
  const [archiveCandidate, setArchiveCandidate] = useState<ContractAttachmentListItemResponseDto | null>(null);

  const handleCreate = () => {
    setEditingAttachment(null);
    setIsFormOpen(true);
  };

  const handleEdit = (item: ContractAttachmentListItemResponseDto) => {
    setEditingAttachment(item);
    setIsFormOpen(true);
  };

  // The presigned URL is short-lived, so it is requested on click instead of with the list.
  const handleViewFile = async (item: ContractAttachmentListItemResponseDto) => {
    const res = await apiRequest<ContractAttachmentFileUrlResponseDto>(`contract-attachments/admin/${item._id}/file`, 'GET');
    if (!res.data) return;
    window.open(res.data.url, '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = async ({ data, file }: AttachmentFormSubmitPayload): Promise<boolean> => {
    if (editingAttachment) {
      const body: UpdateContractAttachmentRequestDto = data;
      const res = await apiRequest<ContractAttachmentResponseDto>(`contract-attachments/admin/${editingAttachment._id}`, 'PATCH', {
        body,
        toast: { toastText: 'Mail-Anhang wurde aktualisiert.' }
      });
      if (res.error) return false;
      await reload();
      return true;
    }

    if (!file) return false;
    const res = await apiFileRequest<ContractAttachmentResponseDto>('contract-attachments/admin', 'POST', file, {
      formdataName: 'file',
      customFields: { title: data.title, fileName: data.fileName },
      toast: { toastText: 'Mail-Anhang wurde hochgeladen.' }
    });
    if (res.error) return false;
    await reload();
    return true;
  };

  const handleArchiveConfirm = async (): Promise<boolean> => {
    if (!archiveCandidate) return false;
    const body: UpdateArchiveRequestDto = { isArchived: !archiveCandidate.isArchived };
    const res = await apiRequest<ContractAttachmentResponseDto>(`contract-attachments/admin/${archiveCandidate._id}/archive`, 'PATCH', {
      body,
      toast: { toastText: body.isArchived ? 'Mail-Anhang wurde archiviert.' : 'Mail-Anhang wurde wiederhergestellt.' }
    });
    if (res.error) return false;
    await reload();
    return true;
  };

  return (
    <div className="flex flex-col gap-5">
      <ListToolbar filter={filter} onFilterChange={handleFilterChange} createLabel="Neuer Mail-Anhang" onCreate={handleCreate} />

      <AttachmentList
        attachments={items}
        filter={filter}
        pagination={pagination}
        pageCount={pageCount}
        onPaginationChange={handlePaginationChange}
        isLoading={isLoading}
        onEdit={handleEdit}
        onViewFile={handleViewFile}
        onArchiveToggle={setArchiveCandidate}
      />

      <AttachmentFormDialog isOpen={isFormOpen} onOpenChange={setIsFormOpen} attachment={editingAttachment} onSubmit={handleSubmit} />

      <ArchiveConfirmDialog
        candidate={archiveCandidate}
        entityLabel="Mail-Anhang"
        onClose={() => setArchiveCandidate(null)}
        onConfirm={handleArchiveConfirm}
      />
    </div>
  );
};

export default AttachmentListWrapper;
